import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import type { Note } from "./types";

export interface KnowledgeSearchOptions {
  debounceMs?: number;
  minLength?: number;
  limit?: number;
  type?: string;
  tag?: string;
  classificationKind?: "tag" | "taxonomy";
  status?: string;
}

export interface KnowledgeSearchState {
  items: Note[];
  total: number;
  active: boolean;
  searching: boolean;
  truncated: boolean;
  error?: string;
}

function isAbortError(error: unknown) {
  return Boolean(error && typeof error === "object" && "name" in error && error.name === "AbortError");
}

function searchErrorMessage(error: unknown) {
  const detail = error instanceof Error ? error.message.trim() : "";
  return detail ? `Could not search knowledge: ${detail}` : "Could not search knowledge. Check the connection and try again.";
}

export function useKnowledgeSearch(query: string, options: KnowledgeSearchOptions = {}): KnowledgeSearchState {
  const {
    debounceMs = 200,
    minLength = 2,
    limit = 20,
    type,
    tag,
    classificationKind,
    status,
  } = options;
  const sequence = useRef(0);
  const [state, setState] = useState<Omit<KnowledgeSearchState, "active">>({
    items: [],
    total: 0,
    searching: false,
    truncated: false,
  });
  const normalizedQuery = query.trim();
  const active = normalizedQuery.length >= minLength;

  useEffect(() => {
    const requestSequence = ++sequence.current;
    const controller = new AbortController();

    if (!active) {
      setState({ items: [], total: 0, searching: false, truncated: false });
      return () => controller.abort();
    }

    setState({ items: [], total: 0, searching: true, truncated: false });
    const timer = window.setTimeout(() => {
      void api.search(normalizedQuery, { type, tag, classificationKind, status, limit }, { signal: controller.signal })
        .then((response) => {
          if (requestSequence !== sequence.current || controller.signal.aborted) return;
          setState({
            items: response.items,
            total: response.total,
            searching: false,
            truncated: Boolean(response.truncated),
          });
        })
        .catch((error: unknown) => {
          if (requestSequence !== sequence.current || controller.signal.aborted || isAbortError(error)) return;
          setState({
            items: [],
            total: 0,
            searching: false,
            truncated: false,
            error: searchErrorMessage(error),
          });
        });
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [active, classificationKind, debounceMs, limit, minLength, normalizedQuery, status, tag, type]);

  return { ...state, active };
}

export function selectPackSearchNotes(localNotes: Note[], rankedNotes: Note[], query: string, limit = 80) {
  const normalized = query.trim().toLocaleLowerCase();
  const candidates = normalized.length >= 2
    ? rankedNotes
    : localNotes.filter((note) => !normalized
      || note.title.toLocaleLowerCase().includes(normalized)
      || note.tags.some((tag) => tag.toLocaleLowerCase().includes(normalized)));
  return candidates.filter((note) => note.status !== "archived").slice(0, limit);
}
